/**
 * When an optional keyboard shortcut may fire.
 *
 * `DESIGN.md`'s rule for these is the owner's own: "visible controls must remain the
 * primary interface" (#41). So a shortcut is a bare letter, never a modified one, and
 * it never fires while somebody is writing — otherwise typing "b" into a search box
 * would throw them onto the board.
 *
 * One function rather than the copy that grew: the Tasks list bound `/ n b l ?` with
 * this guard written inline, and the task page needed the same guard for `a s t`.
 */
export function shortcutsApply(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false
  /*
   * Somebody else has already answered this key, or nobody pressed it twice.
   * `defaultPrevented` is how an overlay, a menu or a field says it has dealt with
   * the press; `repeat` is a held key, and a shortcut that switched the view forty
   * times because a key stuck is not a shortcut.
   */
  if (event.defaultPrevented || event.repeat) return false
  if (somethingIsOpen()) return false
  const target = event.target
  if (!(target instanceof HTMLElement)) return true
  if (target.isContentEditable) return false
  return target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && target.tagName !== 'SELECT'
}

/**
 * Whether a dialog, a popover or a menu is up.
 *
 * A shortcut is for the page, and while one of these is open the page is not what
 * the reader is looking at: with New Task open and the cursor on Close, `b` used to
 * switch the board *behind* the window. The question is asked of the document
 * rather than of a piece of state because every overlay in the portal is one Radix
 * portal on `document.body` (`components/portal/Overlay.tsx`) and no page holds a
 * flag saying so — and a native `<dialog open>` is here for the same reason: it is
 * the one overlay that is not Radix's.
 */
function somethingIsOpen(): boolean {
  if (typeof document === 'undefined') return false
  return (
    document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], dialog[open]') !==
    null
  )
}
